import { LoggingService } from './LoggingService';
import { httpRequest } from '@/lib/http-client';
import { EntityReview } from '../types';

/**
 * Review mechanism types for entity checking
 */
export type ReviewMechanism = 'opensanctions';

/**
 * Service for reviewing entities against compliance databases
 */
export class EntityReviewService {
    private static instance: EntityReviewService;
    private defaultMechanism: ReviewMechanism = 'opensanctions';
    private logger: LoggingService;

    private constructor() {
        this.logger = new LoggingService('EntityReviewService');
        this.logger.info('[Client] EntityReviewService constructed');
        
        // Try to load saved preference from localStorage
        const savedMechanism = localStorage.getItem('complianceReviewMechanism');
        if (savedMechanism === 'opensanctions') {
            this.defaultMechanism = savedMechanism;
            this.logger.info(`[Client] Loaded saved review mechanism from localStorage: ${savedMechanism}`);
        } else {
            // Always default to opensanctions
            localStorage.setItem('complianceReviewMechanism', 'opensanctions');
            this.logger.info('[Client] Set default review mechanism to opensanctions in localStorage');
        }
    }

    public static getInstance(): EntityReviewService {
        if (!EntityReviewService.instance) {
            EntityReviewService.instance = new EntityReviewService();
        }
        return EntityReviewService.instance;
    }

    /**
     * Set the default review mechanism
     * @param mechanism The review mechanism to use by default
     */
    setDefaultMechanism(mechanism: ReviewMechanism): void {
        this.defaultMechanism = mechanism;
        // Save preference to localStorage
        localStorage.setItem('complianceReviewMechanism', mechanism);
        this.logger.info(`[Client] Default review mechanism set to: ${mechanism}`);
    }

    /**
     * Get the current default review mechanism
     * @returns The current default review mechanism
     */
    getDefaultMechanism(): ReviewMechanism {
        return this.defaultMechanism;
    }

    static async createEntityReview(entityId: string, reviewType: string): Promise<EntityReview> {
        try {
            const data = await httpRequest('/api/entity-reviews', {
                method: 'POST',
                data: {
                    entityId,
                    reviewType
                }
            });
            return data;
        } catch (error) {
            console.error('Error creating entity review:', error);
            throw error instanceof Error ? error : new Error('Unknown error occurred');
        }
    }

    static async getEntityReviews(entityId: string): Promise<EntityReview[]> {
        try {
            const data = await httpRequest(`/api/entity-reviews?entityId=${entityId}`, {});
            return data;
        } catch (error) {
            console.error('Error fetching entity reviews:', error);
            throw error instanceof Error ? error : new Error('Unknown error occurred');
        }
    }

    static async updateEntityReview(reviewId: string, updates: Partial<EntityReview>): Promise<void> {
        try {
            await httpRequest(`/api/entity-reviews/${reviewId}`, {
                method: 'PUT',
                data: updates
            });
        } catch (error) {
            console.error('Error updating entity review:', error);
            throw error instanceof Error ? error : new Error('Unknown error occurred');
        }
    }
} 